import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { locationsFindSavedLocation } from "@/Shared/Locations/Helpers/locationsFindSavedLocation";
import { bookShipmentIsWarehouseNotRequiringContact } from "@/Shared/Locations/Helpers/isWarehouseNotRequiringContact";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BookShipmentNavigationFooter2 from "./BookShipmentNavigationFooter2";
import BookShipmentScreenLayout from "../CustomComponents/BookShipmentScreenLayout";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { BookShipmentDeadlinePanel } from "../Panels/BookShipmentDeadlinePanel";
import { useBookShipmentAddressContact } from "../Hooks/useBookShipmentAddressContact";
import { useBookShipmentAddressDeadline } from "../Hooks/useBookShipmentAddressDeadline";
import { useBookShipmentAddressLoadingCapabilities } from "../Hooks/useBookShipmentAddressLoadingCapabilities";
import { useBookShipmentAddressLocationEdits } from "../Hooks/useBookShipmentAddressLocationEdits";
import { useBookShipmentAddressValidation } from "../Hooks/useBookShipmentAddressValidation";
import { useBookShipmentAmazonReference } from "../Hooks/useBookShipmentAmazonReference";
import { useBookShipmentResidentialCheck } from "../Hooks/useBookShipmentResidentialCheck";
import { useBookShipmentAddressSave } from "../Hooks/useBookShipmentAddressSave";
import { BookShipmentAddressContactSection } from "./Internal/bookShipmentAddressContactSection";
import { BookShipmentAddressDetailsSection } from "./Internal/bookShipmentAddressDetailsSection";
import { BookShipmentAddressNotesSection } from "./Internal/bookShipmentAddressNotesSection";
import {
  BookShipmentAddressReferenceSections,
  bookShipmentAddressReferenceSectionCount,
} from "./Internal/bookShipmentAddressReferenceSections";

interface AddressScreenComponentProps {
  identifier: string;

  savedLocations: Array<SavedLocation>;

  shipment: PreBookingShipment;

  context: LocationContext;

  // TODO: Probably just pass in a flow
  nextIdentifier: string;
  onPrevious: () => Promise<void>;

  loading: boolean;

  onComplete: () => void;
  onModify: () => void;

  // undefined until the customer touches the switch — see below.
  markLocationAsFavourite: boolean | undefined;
  onMarkLocationAsFavourite: (newValue: boolean) => void;

  isSameDayPickup: boolean;
}

const BookShipmentAddressScreenComponent: React.FC<
  AddressScreenComponentProps
  // oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
> = (props: AddressScreenComponentProps) => {
  const { loading, context, shipment, savedLocations } = props;

  const isDeliveryAppointment =
    context === LocationContext.Delivery &&
    shipment.deliveryLocation.accessorials.includes(
      "SCHEDULING_APPOINTMENT_REQUIRED",
    );

  const location =
    context === LocationContext.Pickup
      ? shipment.pickupLocation
      : shipment.deliveryLocation;

  const matchingSavedLocation = locationsFindSavedLocation(
    savedLocations,
    location,
  );

  // Show what the address book actually says, so an address the customer
  // already favourited reads as on. Derived rather than seeded when they pick
  // the location, because they can also arrive here by resuming a booking,
  // loading a draft, or editing the business name until it matches.
  //
  // Display only. What gets sent is props.markLocationAsFavourite — the
  // customer's own choice — because this match and the backend's dedup pick
  // independently among duplicate saved locations (both are unordered), so a
  // derived `false` could clear the star on a row we never looked at.
  const favouriteSwitchChecked =
    props.markLocationAsFavourite ??
    matchingSavedLocation?.isFavourite ??
    false;

  const isContactNotRequired = bookShipmentIsWarehouseNotRequiringContact(
    location.distributionWarehouseBrand,
  );

  const { shipmentId, selectedQuote } = shipment;

  const navigate = useNavigate();

  const [requotedWithLiftGate, setRequotedWithLiftGate] = useState(false);

  useEffect(() => {
    const flag = localStorage.getItem("requotedWithLiftGate");
    if (flag === "true") {
      setRequotedWithLiftGate(true);
    }
  }, []);

  const displayName =
    context === LocationContext.Pickup ? "Pickup" : "Delivery";

  const pluralDisplayName =
    context === LocationContext.Pickup ? "pickups" : "deliveries";

  const originalReferenceNumber =
    (context === LocationContext.Pickup
      ? shipment.pickupReferenceNumber
      : shipment.deliveryReferenceNumber) || "";

  const [referenceNumber, setReferenceNumber] = useState(
    originalReferenceNumber,
  );

  const [boothNumber, setBoothNumber] = useState(
    (context === LocationContext.Pickup
      ? shipment.pickupBoothNumber
      : shipment.deliveryBoothNumber) || "",
  );
  const [forceValidation, setForceValidation] = useState(false);

  const addressContact = useBookShipmentAddressContact({
    context,
    shipment,
    matchingSavedLocation,
  });

  const loadingCapabilities = useBookShipmentAddressLoadingCapabilities({
    context,
    location,
    matchingSavedLocation,
  });

  const amazon = useBookShipmentAmazonReference({
    context,
    location,
    originalAmazonReferenceId:
      context === LocationContext.Delivery
        ? shipment.deliveryAmazonReferenceId
        : undefined,
    originalAmazonShipmentId:
      context === LocationContext.Delivery
        ? shipment.deliveryAmazonShipmentId
        : undefined,
  });

  const locationEdits = useBookShipmentAddressLocationEdits(context, location);

  const deadline = useBookShipmentAddressDeadline({
    context,
    location,
    pickupDate: shipment.pickupDate,
    selectedQuote,
  });

  const validation = useBookShipmentAddressValidation({
    context,
    location,
    shipment,
    contact: addressContact.contact,
    referenceNumber,
    boothNumber,
    isDeliveryAppointment,
    isContactNotRequired,
    onModify: props.onModify,
    loadingCapabilities,
    deadline,
    amazon,
  });

  const residentialCheck = useBookShipmentResidentialCheck({
    shipment,
    savedLocations,
    context,
    location,
  });

  useEffect(
    function () {
      if (loading || residentialCheck.addingResidentialRef.current) {
        return;
      }
      if (selectedQuote === undefined) {
        // TODO: Some sort of function for generating these
        console.warn("No quote. Should not be here");
        navigate(`/book/quotes?shipmentId=${props.shipment.shipmentId}`);
      }
    },

    [selectedQuote, loading],
  );

  const nextIsValid = useBookShipmentAddressSave({
    shipmentId,
    context,
    location,
    contact: addressContact.contact,
    isContactNotRequired,
    referenceNumber,
    amazon,
    boothNumber,
    deadlineDate: deadline.deadlineToSubmit,
    markLocationAsFavourite: props.markLocationAsFavourite,
    loadingCapabilities,
    validation,
    residentialCheck,
    setForceValidation,
    onComplete: props.onComplete,
  });
  /////////////////////////////////////////////////////////

  // Guard. Should have already redirected above
  if (selectedQuote === undefined) {
    if (!residentialCheck.addingResidentialRef.current) {
      console.warn("No selected quote, rendering nothing");
    }
    return residentialCheck.dialogs;
  }

  if (loading) {
    return (
      <>
        {residentialCheck.dialogs}
        <PageLoadingSpinner />
      </>
    );
  }

  // Sections are numbered for the user in the order they appear, skipping the
  // ones this location doesn't need.
  let i = 1;
  const addressDetailsIndex = i++;
  const contactIndex = isContactNotRequired ? undefined : i++;
  const referenceStartIndex = i;
  i += bookShipmentAddressReferenceSectionCount(
    amazon.isAmazonDelivery,
    location,
  );
  const notesIndex = i++;

  return (
    <div id={props.context + "-address"}>
      {residentialCheck.dialogs}
      {/* Internals only: the layout mirrors the legacy screen, while the
          controls are ui-core. `underlined` is the Cargo-derived look, so the
          fields render the way this screen always has. */}
      <ControlPresentationProvider presentation="underlined">
        {requotedWithLiftGate && (
          <>
            <div className="w-full max-w-[610px]">
              <Banner variant="info">
                Lift gate has been added to this shipment.
              </Banner>
            </div>
            <div className="h-4" />
          </>
        )}
        <BookShipmentScreenLayout
          pageHeading={displayName + " Address"}
          pageSubheading={
            <>
              <div>
                Please fill in the remaining information below. Take care to be
                accurate, as this information will be used to generate your
                documents.
              </div>
            </>
          }
        >
          <div className="w-full max-w-[610px]">
            {context === LocationContext.Pickup ? (
              <Banner variant="warning">
                Missed pickup charges apply if freight is not ready or no
                shipper is present when driver arrives.
              </Banner>
            ) : (
              <Banner variant="warning">
                Missed deliveries, just like pickups can come with additional
                charges for carrier&apos;s time and resources. The receiver
                should be ready to receive the freight in the stated hours
                during booking.
              </Banner>
            )}
          </div>
          <div className="h-8" />
          <BookShipmentAddressDetailsSection
            index={addressDetailsIndex}
            context={context}
            displayName={displayName}
            location={location}
            shipment={shipment}
            savedLocations={savedLocations}
            contact={addressContact.contact}
            referenceNumber={referenceNumber}
            amazonReferenceId={
              amazon.isAmazonDelivery ? amazon.amazonReferenceId : undefined
            }
            amazonShipmentId={
              amazon.isAmazonDelivery ? amazon.amazonShipmentId : undefined
            }
            boothNumber={boothNumber}
            deadlineDate={deadline.deadlineToSubmit}
            forceValidation={forceValidation}
            isDeliveryAppointment={isDeliveryAppointment}
            isSameDayPickup={props.isSameDayPickup}
            isContactNotRequired={isContactNotRequired}
            favouriteSwitchChecked={favouriteSwitchChecked}
            markLocationAsFavourite={props.markLocationAsFavourite}
            onMarkLocationAsFavourite={props.onMarkLocationAsFavourite}
            onModify={props.onModify}
            locationEdits={locationEdits}
            loadingCapabilities={loadingCapabilities}
            validation={validation}
          />
          <div className="h-8" />

          {contactIndex !== undefined && (
            <BookShipmentAddressContactSection
              index={contactIndex}
              context={context}
              displayName={displayName}
              shipment={shipment}
              matchingSavedLocation={matchingSavedLocation}
              forceValidation={forceValidation}
              addressContact={addressContact}
              validation={validation}
            />
          )}
          <div className="h-8" />
          <BookShipmentAddressReferenceSections
            startIndex={referenceStartIndex}
            context={context}
            displayName={displayName}
            pluralDisplayName={pluralDisplayName}
            location={location}
            shipmentId={shipmentId}
            originalReferenceNumber={originalReferenceNumber}
            referenceNumber={referenceNumber}
            onReferenceNumberChange={setReferenceNumber}
            boothNumber={boothNumber}
            onBoothNumberChange={setBoothNumber}
            forceValidation={forceValidation}
            validation={validation}
            amazon={amazon}
          />
          {deadline.showDeadlineSection && (
            <BookShipmentDeadlinePanel
              titleContext={displayName}
              pickupDate={shipment.pickupDate}
              earliestDeliveryDate={deadline.earliestDeliveryDate}
              deadlineDate={deadline.deadlineDate}
              onDeadlineDateChange={deadline.onDeadlineDateChange}
              deadlineErrors={deadline.deadlineErrors}
              hasDeadline={deadline.hasDeadline}
              onHasDeadlineChange={deadline.onHasDeadlineChange}
              deadlineIsRequired={deadline.deadlineIsRequired}
              forceValidation={forceValidation}
            />
          )}
          <div className="h-8" />
          <BookShipmentAddressNotesSection
            index={notesIndex}
            displayName={displayName}
            notes={location.notes}
            onNotesChange={locationEdits.setNotes}
            errorMessage={validation.notesErrorMessage}
            forceValidation={forceValidation}
          />
          <BookShipmentNavigationFooter2
            next="Next"
            onPrevious={props.onPrevious}
            nextUrl={`/book/${props.nextIdentifier}?shipmentId=${shipmentId}`}
            nextIsValid={nextIsValid}
            nextIsEnabled={() => !loadingCapabilities.needsLiftGate()}
            nextIdentifier={props.nextIdentifier}
            hidePreviousButton={false}
          />
        </BookShipmentScreenLayout>
      </ControlPresentationProvider>
    </div>
  );
};
export default BookShipmentAddressScreenComponent;
